import { CommonContentPageTranslator } from 'src/translators'
import {
  CommonContentPageEntity,
  CommonContentPageFormEntity,
  CommonContentPageRequest,
} from 'src/types'
import {
  useCreateCommonContentPageMutation,
  useUpdateCommonContentPageMutation,
  useDeleteCommonContentPageMutation,
  useReorderCommonContentPagesMutation,
} from 'src/types/generated'

type CreateCommonContentPageInput = {
  pageOrder: number
}

type UpdateCommonContentPageInput = {
  commonContentPageId: number
  formEntity: CommonContentPageFormEntity
}

type UpdateCommonContentIconInput = {
  commonContentPage: CommonContentPageEntity
  newIconId: number
}

type DeleteCommonContentPageInput = {
  commonContentPageId: number
}

type ReorderCommonContentPagesInput = {
  updatedCommonContentPageIds: number[]
}

export const useCommonContentPage = () => {
  const [createCommonContentPageMutation] = useCreateCommonContentPageMutation()
  const [updateCommonContentPageMutation] = useUpdateCommonContentPageMutation()
  const [deleteCommonContentPageMutation] = useDeleteCommonContentPageMutation()
  const [reorderCommonContentPagesMutation] = useReorderCommonContentPagesMutation()

  const createCommonContentPage = async ({ pageOrder }: CreateCommonContentPageInput) => {
    const input: CommonContentPageRequest = {
      name: `共通コンテンツ ${pageOrder}`,
      priority: pageOrder,
      meta: {
        isPublic: false,
        displayConsumer: false,
        designFormat: 'gallery',
        icon: 22,
      },
    }
    await createCommonContentPageMutation({ variables: { params: input } })
  }

  const updateCommonContentPage = async ({
    commonContentPageId,
    formEntity,
  }: UpdateCommonContentPageInput) => {
    const input: CommonContentPageRequest = CommonContentPageTranslator.toRequest(formEntity)
    await updateCommonContentPageMutation({
      variables: { commonPageId: commonContentPageId, params: input },
    })
  }

  const updateCommonContentIcon = async ({
    commonContentPage,
    newIconId,
  }: UpdateCommonContentIconInput) => {
    const formEntity = CommonContentPageTranslator.toFormEntity(commonContentPage)
    const updateFormEntity = { ...formEntity, icon: newIconId }
    const input: CommonContentPageRequest = CommonContentPageTranslator.toRequest(updateFormEntity)
    await updateCommonContentPageMutation({
      variables: { commonPageId: commonContentPage.id, params: input },
    })
  }

  const deleteCommonContentPage = async ({ commonContentPageId }: DeleteCommonContentPageInput) => {
    await deleteCommonContentPageMutation({ variables: { CommonPageId: commonContentPageId } })
  }

  const reorderCommonContentPages = async ({
    updatedCommonContentPageIds,
  }: ReorderCommonContentPagesInput) => {
    await reorderCommonContentPagesMutation({
      variables: { params: { commonPagesPriorities: updatedCommonContentPageIds } },
    })
  }

  const toCommonContentPageFormEntity = (
    entity: CommonContentPageEntity
  ): CommonContentPageFormEntity => {
    return CommonContentPageTranslator.toFormEntity(entity)
  }

  return {
    createCommonContentPage,
    updateCommonContentPage,
    updateCommonContentIcon,
    deleteCommonContentPage,
    reorderCommonContentPages,
    toCommonContentPageFormEntity,
  }
}
